import { Link } from 'react-router';
import { useBillingStatus } from 'markcat-api-client';
import styles from './PlanBadge.module.css';

// Small clickable pill in the sidebar footer that shows the current plan
// and routes to /app/billing on click. A warning variant appears during the
// grace period so an imminent downgrade is visible while browsing, not only
// on the Billing page.

function planDisplay(plan: string): { label: string; variant: string } {
  if (plan === 'pro') return { label: 'Pro', variant: styles.pro };
  if (plan === 'basic') return { label: 'Basic', variant: styles.basic };
  return { label: 'Free', variant: styles.free };
}

function daysUntil(expiresAt: number): number | null {
  const now = Math.floor(Date.now() / 1000);
  if (expiresAt <= now) return null;
  return Math.ceil((expiresAt - now) / 86400);
}

export function PlanBadge() {
  const { data } = useBillingStatus();
  if (!data) return null;

  const { label, variant } = planDisplay(data.effective_plan);
  const graceDays = data.plan_expires_at ? daysUntil(data.plan_expires_at) : null;
  const inGrace = graceDays !== null;

  return (
    <Link
      to="/app/billing"
      className={`${styles.badge} ${variant} ${inGrace ? styles.grace : ''}`}
      aria-label={
        inGrace
          ? `Current plan: ${label}. Ends in ${graceDays} day${graceDays === 1 ? '' : 's'}. Click to manage.`
          : `Current plan: ${label}. Click to manage.`
      }
    >
      <span>{label}</span>
      {inGrace && (
        <span className={styles.graceIndicator} aria-hidden="true">
          · ends in {graceDays}d
        </span>
      )}
    </Link>
  );
}
